import { useEffect, useState } from "react";
import api from "@/api";
import { useI18n } from "@/i18n";
import { Bell, BellOff, BellRing } from "lucide-react";
import { toast } from "sonner";

function urlB64ToUint8Array(base64) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

export default function PushToggle() {
  const { t } = useI18n();
  const [state, setState] = useState("hidden"); // hidden | off | on | denied
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) {
      const isiOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
      const standalone = window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone;
      if (isiOS && !standalone) setState("install");
      return;
    }
    if (Notification.permission === "denied") { setState("denied"); return; }
    navigator.serviceWorker.getRegistration().then(async (reg) => {
      const sub = reg && (await reg.pushManager.getSubscription());
      setState(sub ? "on" : "off");
    }).catch(() => setState("off"));
  }, []);

  const enable = async () => {
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { setState("denied"); toast.error(t("pushDenied")); return; }
      const reg = (await navigator.serviceWorker.getRegistration()) || (await navigator.serviceWorker.register("/sw.js"));
      await navigator.serviceWorker.ready;
      const { key } = await api.pushVapidKey();
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlB64ToUint8Array(key) });
      await api.pushSubscribe(sub.toJSON());
      setState("on");
      toast.success(t("pushOn"));
      api.pushTest().catch(() => {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("pushError"));
    } finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = reg && (await reg.pushManager.getSubscription());
      if (sub) { await api.pushUnsubscribe(sub.endpoint).catch(() => {}); await sub.unsubscribe(); }
      setState("off");
      toast.success(t("pushOff"));
    } catch (e) { toast.error(t("pushError")); } finally { setBusy(false); }
  };

  if (state === "hidden") return null;
  if (state === "install") {
    return (
      <div className="flex items-start gap-2 text-xs text-white/50" data-testid="push-install-hint">
        <Bell className="w-4 h-4 shrink-0 mt-0.5" /> {t("pushInstallHint")}
      </div>
    );
  }
  if (state === "denied") {
    return (
      <div className="flex items-center gap-2 text-xs text-white/40" title={t("pushDeniedHint")} data-testid="push-denied">
        <BellOff className="w-4 h-4" /> {t("pushDenied")}
      </div>
    );
  }
  return state === "on" ? (
    <button onClick={disable} disabled={busy} className="flex items-center gap-2 text-xs text-[#C5A880] hover:text-white transition-colors" data-testid="push-toggle-off">
      <BellRing className="w-4 h-4" /> {t("pushOnLbl")}
    </button>
  ) : (
    <button onClick={enable} disabled={busy} className="flex items-center gap-2 text-xs text-white/50 hover:text-white transition-colors" data-testid="push-toggle-on">
      <Bell className="w-4 h-4" /> {busy ? "…" : t("pushEnable")}
    </button>
  );
}
